// The drag order preview (R1208, ui-map-interface.md §8 "Drag order").
//
// While a finger drags an own fleet, this layer draws the order the release
// will give: a dashed line from the hull to the order point, and the
// crosshair mark of §8 at the end of it. The line and the mark wear the
// colour of the order (`dragInk`), and the mark wears the snap ring when the
// point is a target's centre and not the finger's.
//
// It draws on the two batches every line of the map already uses, so it
// needs no shader of its own and draws on both GL backends (R847). The line
// and the mark take `F_SCREEN`, so both hold one size at every zoom; the arm
// endpoints are the one thing the shader does not scale, so the pack places
// them at the screen offsets divided by the zoom, as `crosshair.ts` does.
//
// The cost follows `crosshair.ts`: `set` that repeats its arguments packs
// nothing, and a frame at an unchanged zoom re-flushes the last pack. The
// mark is the kept shape of `crosshairAtOrigin`, so a pack allocates none.
//
// The on-ramp offer (R3075, §8 "The on-ramp offer"): while a MOVE goes to
// a point that a lane of the faction serves faster, `setLane` hands the
// layer the lane course, and the layer draws it dashed in the faction ink
// at `FLEET_COURSE.laneAlpha`, under the direct line, which the release
// gives. A lit stretch of either line wears the band of the faction ink
// (`litWidth`, `litAlpha`, §6.1). The page computes the course at the
// cadence of the drag decision, never once a frame.

import { FLEET_COURSE, FLEET_CROSSHAIR, FLEET_ORDER_DRAG } from '../../gen/presentation';
import { crosshairAtOrigin } from '../../render/crosshair-paths';
import { dragInk, type ContextKind } from '../dragorder';
import { F_SCREEN, type DrawCtx, type FrameInfo, type Layer } from '../layer';
import type { BatchSource, CircleSink, SegmentSink } from './batches';
import { zoomOf } from './shared';
import { packArms } from './crosshair';

/** The line, the four arms of the mark, a lane course and its lit bands. */
const MAX_SEGMENTS = 64;

/** The lane course of the on-ramp offer under a drag (R3075): the hull,
 *  the legs of the course, the lit runs (x0, y0, x1, y1) of the course and
 *  of the direct line, and the faction ink. */
export interface DragLane {
  readonly legs: readonly { readonly x: number; readonly y: number }[];
  readonly runs: readonly number[];
  readonly ink: number;
}
/** The snap ring. */
const MAX_RINGS = 1;

export class OrderDragLayer implements Layer {
  readonly name = 'orderdrag';

  private readonly segments: SegmentSink;
  private readonly circles: CircleSink;
  private shown = true;

  // The last preview asked for. A still finger repeats it every frame.
  private on = false;
  private fx = 0;
  private fy = 0;
  private tx = 0;
  private ty = 0;
  private kind: ContextKind = 'move';
  private snapped = false;
  private lane: DragLane | null = null;

  private dirty = false;
  /** The zoom the packed instances were placed at. */
  private zoom = 0;
  /** How many times the layer packed. The tests read it. */
  packs = 0;

  constructor(src: BatchSource) {
    this.segments = src.newSegments(MAX_SEGMENTS);
    this.circles = src.newCircles(MAX_RINGS);
  }

  /**
   * Draws the preview from a hull at (fx, fy) to an order point at (tx, ty),
   * both in world px.
   */
  set(fx: number, fy: number, tx: number, ty: number, kind: ContextKind, snapped: boolean): void {
    if (this.on && fx === this.fx && fy === this.fy && tx === this.tx && ty === this.ty
      && kind === this.kind && snapped === this.snapped) return;
    this.on = true;
    this.fx = fx;
    this.fy = fy;
    this.tx = tx;
    this.ty = ty;
    this.kind = kind;
    this.snapped = snapped;
    this.dirty = true;
  }

  /** The lane course of the on-ramp offer and the lit runs, or null. The
   *  page hands the same object while nothing changed, so a repeat packs
   *  nothing. */
  setLane(lane: DragLane | null): void {
    if (lane === this.lane) return;
    this.lane = lane;
    this.dirty = true;
  }

  /** The lane course the preview draws, or null. The tests read it. */
  get laneDrawn(): DragLane | null { return this.on ? this.lane : null; }

  /** No drag is live: the layer draws nothing, and a repeat costs nothing. */
  clear(): void {
    if (!this.on) return;
    this.on = false;
    this.lane = null;
    this.dirty = true;
  }

  /** Is a preview on the glass? The tests and `__debug` read it. */
  get live(): boolean { return this.on; }
  /** The kind the preview draws. */
  get drawn(): ContextKind { return this.kind; }

  update(frame: FrameInfo): void {
    const zoom = frame.cam.zoom;
    if (!this.dirty && zoom === this.zoom) return;
    this.dirty = false;
    this.zoom = zoom;
    this.pack(zoom);
  }

  draw(ctx: DrawCtx): void {
    this.segments.flush(ctx.cam);
    this.circles.flush(ctx.cam);
  }

  setVisible(v: boolean): void { this.shown = v; }
  get visible(): boolean { return this.shown; }

  private pack(zoom: number): void {
    const s = this.segments;
    const c = this.circles;
    s.begin();
    c.begin();
    this.packs++;
    if (!this.on) return;
    const d = FLEET_ORDER_DRAG;
    const x = this.tx;
    const y = this.ty;
    const color = dragInk(this.kind);
    const k = 1 / zoomOf(zoom);
    const lane = this.lane;
    if (lane) {
      // The bands first, so the dashes stand on them; then the lane course.
      const c = FLEET_COURSE;
      for (let i = 0; i + 3 < lane.runs.length; i += 4) {
        s.line(lane.runs[i], lane.runs[i + 1], lane.runs[i + 2], lane.runs[i + 3], c.litWidth, lane.ink, c.litAlpha, F_SCREEN);
      }
      let ax = this.fx, ay = this.fy;
      for (const p of lane.legs) {
        s.line(ax, ay, p.x, p.y, d.width, lane.ink, c.laneAlpha, F_SCREEN, d.dashOnPx, d.dashOffPx);
        ax = p.x; ay = p.y;
      }
    }
    // The line stops where the mark begins, so it never crosses the empty
    // middle the mark keeps over the target (§8).
    const stop = (this.snapped ? FLEET_CROSSHAIR.snapRingR : FLEET_CROSSHAIR.gapPx) * k;
    const dx = x - this.fx;
    const dy = y - this.fy;
    const len = Math.hypot(dx, dy);
    if (len > stop) {
      const f = (len - stop) / len;
      s.line(this.fx, this.fy, this.fx + dx * f, this.fy + dy * f,
        d.width, color, d.alpha, F_SCREEN, d.dashOnPx, d.dashOffPx);
    }
    const path = crosshairAtOrigin('move', this.snapped);
    const cfg = FLEET_CROSSHAIR;
    packArms(s, path.arms, x, y, k, color);
    if (path.snapR > 0) {
      c.ring(x, y, path.snapR, cfg.snapRingWidth, color, cfg.snapRingAlpha, F_SCREEN);
    }
  }
}
